import styles from './edit-focus-account-dialog.module.scss';
import { ContactListContext, GetCurrentCompany } from '../../customer-context/customer-context';
import { Col, Row } from 'reactstrap';
import { useGetRequestNewHook, useMakeRequestHook } from '../../requestUtils';
import { useContext, useEffect, useState } from 'react';
import {
  Button, ButtonGroup, CheckboxLabel, CompanySearch, Dialog, DialogActions, DialogContent, DialogTitle, GrayOutlinedButton,
  IContactType, IUserAccount, PrimaryButton, SecondaryButton, SnackBarContext, TextField
} from '@frontend-workspace/react-shared-lib';
import { useTranslation } from 'react-i18next';

/* eslint-disable-next-line */
export interface EditFocusAccountDialogProps {
  open: boolean,
  onClose: () => void,
  getUserAccounts: () => void,
  id: string
}
export interface EditFocusAccountDialogContentProps {
  open: boolean,
  onClose: () => void,
  getUserAccounts: () => void,
  id: string,
  userAccount: IUserAccount
}
enum EditFocusAccountDialogState {
  Edit,
  ChangeEmail,
  ChangeCompany
}
export function EditFocusAccountDialog(props: EditFocusAccountDialogProps) {
  const currentCompany = GetCurrentCompany();
  const { data: userAccount }: { data: IUserAccount } = useGetRequestNewHook(`accounts/${props.id}`, undefined, [currentCompany]);
  return userAccount ? <EditFocusAccountDialogContent {...props} userAccount={userAccount} /> : null;
}
export function EditFocusAccountDialogContent(props: EditFocusAccountDialogContentProps) {
  const { t } = useTranslation();
  const currentCompany = GetCurrentCompany();
  const makeRequest = useMakeRequestHook();
  const userAccount = props.userAccount;
  const [phoneNumber, setPhoneNumber] = useState<string | undefined>(userAccount?.phoneNumber);
  const [isBlocked, setIsBlocked] = useState<boolean>(userAccount?.isBlocked);
  const [isRetired, setIsRetired] = useState<boolean>(userAccount?.isRetired);
  const [isAcademic, setIsAcademic] = useState<boolean>(userAccount?.isAcademic);
  const [isTwoFactorEnabled, setIsTwoFactorEnabled] = useState<boolean>(userAccount?.twoFactorEnabled);
  const [retireInSO, setRetireInSO] = useState<boolean>(false);

  const [content, setContent] = useState<EditFocusAccountDialogState>(EditFocusAccountDialogState.Edit);
  const { setSnackbarMessage } = useContext(SnackBarContext);
  const isDeleted = userAccount?.email.includes("DELETED");

  const handleIsBlocked = (event: React.ChangeEvent<Element>, checked: boolean) => setIsBlocked(checked);
  const handleIsAcademic = (event: React.ChangeEvent<Element>, checked: boolean) => setIsAcademic(checked);
  const handleIsTwoFactorEnabled = () => setIsTwoFactorEnabled(!isTwoFactorEnabled);
  useEffect(() => {
    if (userAccount) {
      setPhoneNumber(userAccount.phoneNumber);
      setIsBlocked(userAccount.isBlocked);
      setIsRetired(userAccount.isRetired);
      setIsAcademic(userAccount.isAcademic);
    }
  }, [userAccount]);

  const handleEditAccount = () => {
    makeRequest(`accounts`, 'PUT', {
      isBlocked: isBlocked,
      isStudent: isAcademic,
      phone: phoneNumber,
      userGID: userAccount.id,
      email: userAccount.email,
      isWebkuaOrder: true,
      twoFactorEnabled: isTwoFactorEnabled,
      retireInSO: retireInSO
    }).then(result => {
      setSnackbarMessage({ content: t('standard.changesFocusAccount'), type: "info", });
      props.onClose();
    }).catch(() => setSnackbarMessage({ content: t('standard.couldNotChangeFocusAccount'), type: "error" }));
  };

  const handleReopen = () => {
    makeRequest(`accounts/${props.id}/action/reopen`, 'PUT')
      .then(result => {
        setSnackbarMessage({ content: t('editFocusAccount.reopenedFocusAccount'), type: "success", });
        props.onClose();
      }).catch(() => setSnackbarMessage({ content: t('editFocusAccount.couldNotReopenFocusAccount'), type: "error" }));
  };

  const handleResetMfa = () => {
    makeRequest(`accounts/${userAccount.id}/action/deleteUserMfa`, 'DELETE')
      .then(result => {
        setSnackbarMessage({ content: t('editFocusAccount.deletesUserMfa'), type: "info" });
        props.onClose();
      });
  }

  const handleSetAsAdmin = () => {
    makeRequest(`accounts/${userAccount.id}/action/setAdmin/false`, 'PUT')
      .then(result => {
        setSnackbarMessage({ content: t('editFocusAccount.changedAdmin'), type: "success" });
        props.getUserAccounts();
        props.onClose();
      });
  };

  const handleSetAsCoAdmin = () => {
    makeRequest(`accounts/${userAccount.id}/action/setAdmin/true`, 'PUT')
      .then(result => {
        setSnackbarMessage({ content: t('editFocusAccount.changedToCoAdmin'), type: "success" });
        props.getUserAccounts();
        props.onClose();
      });
  };

  const handleSetAsUser = () => {
    makeRequest(`accounts/${userAccount.id}/action/setUser`, 'PUT')
      .then(result => {
        setSnackbarMessage({ content: t('editFocusAccount.changedToUser'), type: "success" });
        props.getUserAccounts();
        props.onClose();
      });
  };

  const handleResendInvitation = () => {
    makeRequest(`accounts/${userAccount.email}/action/ResendInvitation/`, 'PUT')
      .then(result => {
        setSnackbarMessage({ content: t('editFocusAccount.resendingInvitation'), type: "info" });
        props.getUserAccounts();
        props.onClose();
      });
  };

  const handleUserDelete = () => {
    makeRequest(`accounts/${props.id}/sendEmail/false`, 'DELETE')
      .then(result => {
        setSnackbarMessage({ content: t('editFocusAccount.deletesFocusAccount'), type: "info", });
        props.onClose();
      });
  };

  const handleUserDeleteWithEmail = () => {
    makeRequest(`accounts/${props.id}/sendEmail/true`, 'DELETE').then(result => {
      setSnackbarMessage({ content: t('editFocusAccount.deletesFocusAccount'), type: "info", });
      props.onClose();
    });
  };


  const dialogContent = content === EditFocusAccountDialogState.ChangeEmail ?
    <ChangeEmail
      email={userAccount.email}
      companyNumber={userAccount.companyNumber}
      onClose={() => setContent(EditFocusAccountDialogState.Edit)}
      onDialogClose={() => props.onClose()}
    /> : content === EditFocusAccountDialogState.ChangeCompany && currentCompany ?
      <ChangeCompany
        email={userAccount.email}
        onClose={() => setContent(EditFocusAccountDialogState.Edit)}
        currentCompany={currentCompany}
        onDialogClose={() => props.onClose()}
      /> :
      <>
        <DialogTitle className='mb-4'>
          {t('editFocusAccount.changeFocusAccount')}: <span className='MuiTypography-body3'>{userAccount?.email}</span>
        </DialogTitle>
        <DialogContent>
          <div className="d-flex flex-column mb-3">
            <Row className="align-items-end">
              <Col md={8}>
                <div className='mb-3'>
                  <TextField
                    value={userAccount?.email || ''}
                    fullWidth
                    label={t('standard.focusAccount')}
                    name='email'
                    disabled
                  />
                </div>
              </Col>
              <Col md={4}>
                <div className='mb-3'>
                  <SecondaryButton
                    variant='outlined'
                    onClick={() => setContent(EditFocusAccountDialogState.ChangeEmail)}>
                    {t('standard.changeEmail')}
                  </SecondaryButton>
                </div>
              </Col>
            </Row>
            <Row className="align-items-end">
              <Col md={8}>
                <div className='mb-3'>
                  <TextField
                    value={`${userAccount?.companyName} (${userAccount?.companyNumber})` || ''}
                    fullWidth
                    label={t('standard.company')}
                    name='companyName'
                    disabled
                  />
                </div>
              </Col>
              <Col md={4}>
                <div className='mb-3'>
                  <SecondaryButton
                    variant='outlined'
                    onClick={() => setContent(EditFocusAccountDialogState.ChangeCompany)}>
                    {t('editFocusAccount.changeCompany')}
                  </SecondaryButton>
                </div>
              </Col>
            </Row>
            <Row>
              <Col md={8}>
                <div className='mb-3'>
                  <TextField
                    value={userAccount?.firstName || ''}
                    fullWidth
                    label={t('standard.firstName')}
                    name='firstName'
                    disabled
                  />
                </div>
              </Col>
            </Row>
            <Row>
              <Col md={8}>
                <div className='mb-3'>
                  <TextField
                    value={userAccount?.lastName || ''}
                    fullWidth
                    label={t('standard.lastName')}
                    name='lastName'
                    disabled
                  />
                </div>
              </Col>
            </Row>
            <Row>
              <Col xs={6} md={4}>
                <div className='mb-3'>
                  <TextField
                    fullWidth
                    type="tel"
                    label={t('standard.phone')}
                    name='phoneNumber'
                    value={phoneNumber || ''}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                  />
                </div>
              </Col>
            </Row>
            <div className='mb-3'>
              <CheckboxLabel
                label={t('standard.blocked')}
                checked={isBlocked || false}
                disabled
                onChange={handleIsBlocked}
              />
              <CheckboxLabel
                label={t('standard.retired')}
                disabled
                checked={isRetired || false}
                onChange={handleIsBlocked}
              />
              <CheckboxLabel
                label={t('standard.academic')}
                checked={isAcademic || false}
                onChange={handleIsAcademic}
              />
              <CheckboxLabel
                label={t('standard.twoFactorEnabled')}
                checked={isTwoFactorEnabled || false}
                onChange={handleIsTwoFactorEnabled}
              />
            </div>
          </div>

          <div className="d-flex flex-row flex-wrap align-items-end">
            <div className="d-flex flex-row mb-3">
              <div className='me-3 mb-3 mb-lg-0'>
                {userAccount?.role !== 5 &&
                  <ButtonGroup
                    variant='outlined'
                    color='error'
                    size='small'>
                    <Button
                      onClick={handleResetMfa}>
                      {t('editFocusAccount.resetMfa')}
                    </Button>
                  </ButtonGroup>
                }
              </div>
            </div>
            <div className="d-flex flex-row flex-wrap mb-3">
              <div className='me-3 mb-3 mb-lg-0'>
                {userAccount?.role !== 5 &&
                  <Button
                    variant='outlined'
                    color='secondary'
                    size='small'
                    onClick={handleSetAsUser}>
                    {t('editFocusAccount.selectAsUser')}
                  </Button>
                }
              </div>
              <div className='me-3'>
                <ButtonGroup
                  variant='outlined'
                  color='secondary'
                  size='small'>
                  {userAccount?.role !== 5 ? <>
                    {/* <Button
                      onClick={handleSetAsUser}>
                      {t('editFocusAccount.selectAsUser')}
                    </Button> */}
                    <Button
                      onClick={handleSetAsAdmin}>
                      {t('editFocusAccount.selectAsAdmin')}
                    </Button>
                    <Button
                      onClick={handleSetAsCoAdmin}>
                      {t('editFocusAccount.selectAsCoAdmin')}
                    </Button>
                  </> :
                    <Button
                      onClick={handleResendInvitation}>
                      {t('editFocusAccount.resendInvitation')}
                    </Button>}
                </ButtonGroup>
              </div>
            </div>
            <div className="d-flex flex-row mb-3">
              <div className='me-3 me-xl-0'>
                {isDeleted ?
                  <SecondaryButton
                    variant='outlined'
                    size='small'
                    onClick={handleReopen}>
                    {t('editFocusAccount.reopenFocusAccount')}
                  </SecondaryButton> :
                  <div>
                    <ButtonGroup
                      variant='outlined'
                      color='error'
                      size='small'>
                      <Button
                        onClick={handleUserDelete}>
                        {t('editFocusAccount.deleteWithoutEmail')}
                      </Button>
                      <Button
                        onClick={handleUserDeleteWithEmail}>
                        {t('editFocusAccount.deleteWithEmail')}
                      </Button>
                    </ButtonGroup>
                  </div>
                }
              </div>
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          {/* <Divider orientation="vertical" variant="fullWidth" flexItem className="mx-4 d-none d-xl-block" /> */}
          <div className="d-flex flex-row mb-3 mb-xxl-0">
            <div className='me-3'>
              <PrimaryButton
                variant="contained"
                size='large'
                onClick={handleEditAccount}
                className='minButtonWidth'>
                OK
              </PrimaryButton>
            </div>
            <div>
              <GrayOutlinedButton
                variant="outlined"
                size='large'
                onClick={props.onClose}
                className='minButtonWidth'>
                {t('standard.cancel')}
              </GrayOutlinedButton>
            </div>
          </div>
        </DialogActions>
      </>;

  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      maxWidth="xl"
      fullWidth>
      {userAccount && dialogContent}
    </Dialog>
  );
}

function ChangeCompany(props: {
  email: string,
  currentCompany: IContactType,
  onDialogClose: () => void,
  onClose: () => void
}) {
  const companyList = useContext(ContactListContext);
  const { t } = useTranslation();
  const makeRequest = useMakeRequestHook();
  const [companyNumber, setCompanyNumber] = useState<string>(props.currentCompany.number);

  const handleCompanySelected = (companyNumber: string) => {
    const company = companyList.find(c => c.number === companyNumber);
    if (!company) return;
    setCompanyNumber(company?.number);
  };

  const handleChangeCompany = () => {
    makeRequest(`accounts/action/changeCompany/`, 'PUT', {
      newCompanyNumber: companyNumber,
      email: props.email
    }).then(result => props.onDialogClose());
  };

  return <>
    <DialogTitle className='mb-4'>
      {t('editFocusAccount.changeCompanyFor')} {props.email}
    </DialogTitle>
    <DialogContent>
      <CompanySearch
        selectedCompanyNumber={companyNumber}
        companyList={companyList}
        onChange={handleCompanySelected}
      />
    </DialogContent>
    <DialogActions>
      <div className="d-flex flex-row">
        <div className='mb-0 me-3'>
          <PrimaryButton
            variant="contained"
            size='large'
            onClick={handleChangeCompany}
            className='minButtonWidth'>
            {t('editFocusAccount.moveUserToNewCompany')}
          </PrimaryButton>
        </div>
        <div className='mb-0'>
          <GrayOutlinedButton
            variant="outlined"
            size='large'
            onClick={props.onClose}
            className='minButtonWidth'>
            {t('standard.cancel')}
          </GrayOutlinedButton>
        </div>
      </div>
    </DialogActions>
  </>
}

function ChangeEmail(props: {
  email: string,
  companyNumber: string,
  onClose: () => void,
  onDialogClose: () => void
}) {
  const { t } = useTranslation();
  const makeRequest = useMakeRequestHook();
  const [email, setEmail] = useState<string>(props.email);

  const handleEditAccount = () => {
    makeRequest(`accounts/action/changeEmail/`, 'PUT', {
      existingEmail: props.email,
      newEmail: email,
      companyNumber: props.companyNumber
    }).then(result => props.onDialogClose());
  };

  return <>
    <DialogTitle className='mb-4'>
      {t('editFocusAccount.changeEmailFor')} {props.email}
    </DialogTitle>
    <DialogContent>
      <div className='mb-3'>
        <TextField
          value={email}
          fullWidth
          label={t('standard.focusAccount')}
          name='email'
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>
    </DialogContent>
    <DialogActions>
      <div className="d-flex flex-row">
        <div className='mb-0 me-3'>
          <PrimaryButton
            variant="contained"
            size='large'
            onClick={handleEditAccount}
            className='minButtonWidth'>
            {t('standard.changeEmail')}
          </PrimaryButton>
        </div>
        <div className='mb-0'>
          <GrayOutlinedButton
            variant="outlined"
            size='large'
            onClick={props.onClose}
            className='minButtonWidth'>
            {t('standard.cancel')}
          </GrayOutlinedButton>
        </div>
      </div>
    </DialogActions>
  </>;
}

export default EditFocusAccountDialog;